"use client";

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const FinalCTASection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const trustIndicators = [
    { id: 'trust_card', icon: 'CreditCardIcon', text: 'No credit card required' },
    { id: 'trust_messages', icon: 'ChatBubbleLeftRightIcon', text: 'First 3 messages free' },
    { id: 'trust_cancel', icon: 'XCircleIcon', text: 'Cancel anytime' },
  ];

  return (
    <section id="start" ref={sectionRef} className="py-32 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        {/* Main Content */}
        <div
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <h2 className="font-jakarta font-bold text-4xl md:text-6xl text-white mb-6 leading-tight tracking-tight">
            Start Preserving Your Legacy Today
          </h2>
          <p className="text-white/90 text-xl md:text-2xl leading-relaxed mb-12 max-w-2xl mx-auto">
            Create meaningful messages that will be delivered exactly when you choose. Your final words, preserved forever.
          </p>

          {/* CTA Button */}
          <Link
            href="/old-landing-page#signup"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-primary rounded-full font-bold text-lg hover:bg-white/95 transition-all shadow-2xl hover:shadow-white/20 hover:scale-105 group"
          >
            Start Your Legacy
            <Icon
              name="ArrowRightIcon"
              size={24}
              className="group-hover:translate-x-2 transition-transform"
            />
          </Link>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 mt-12">
            {trustIndicators.map((indicator) => (
              <div
                key={indicator.id}
                className="flex items-center gap-2 text-white/80 text-sm"
              >
                <Icon name={indicator.icon as any} size={20} className="text-white" />
                <span>{indicator.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Trust Badges */}
        <div
          className={`mt-16 pt-12 border-t border-white/20 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-wrap justify-center gap-8 text-white/80">
            <div className="flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={24} className="text-white" />
              <span className="text-sm font-medium">256-bit Encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="UserGroupIcon" size={24} className="text-white" />
              <span className="text-sm font-medium">5,000+ Families Trust Us</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckBadgeIcon" size={24} className="text-white" />
              <span className="text-sm font-medium">PIPA Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;